/**
 * Autonomous AI Marketing Agent
 * 
 * This is the brain of the autonomous marketing system. It:
 * - Monitors KPIs continuously
 * - Analyzes trends and opportunities
 * - Makes strategic decisions
 * - Executes actions across channels
 * - Learns from results
 */

import { supabase } from './supabase';
import { AutomationEngine } from './automation-engine';

// ============================================================================
// TYPES
// ============================================================================

export interface AgentConfig {
  company_id: string;
  autonomy_mode: 'supervised' | 'semi_autonomous' | 'fully_autonomous';
  auto_approve_threshold: number;
  daily_action_limit: number;
  monitoring_frequency_hours: number;
  alert_email_addresses: string[];
  primary_goals: string[];
  openai_api_key?: string;
}

export interface KPISnapshot {
  brand_key: string;
  category: string;
  metric: string;
  current_value: number;
  previous_value: number | null;
  change_pct: number | null;
  trend: 'improving' | 'declining' | 'stable' | 'volatile';
  alert_status: 'normal' | 'warning' | 'critical';
}

export interface AIDecision {
  decision_type: string;
  confidence_score: number;
  decision_summary: string;
  recommended_actions: Action[];
  reasoning: string;
  expected_impact: 'high' | 'medium' | 'low';
  expected_roi?: number;
}

export interface Action {
  action_type: string;
  action_config: Record<string, any>;
  content_title?: string;
  content_body?: string;
  target_platform?: string;
  scheduled_for?: Date;
}

// ============================================================================
// AI MARKETING AGENT CLASS
// ============================================================================

export class AIMarketingAgent {
  private config: AgentConfig;
  private isMonitoring: boolean = false;
  private monitoringInterval: NodeJS.Timeout | null = null;

  constructor(config: AgentConfig) {
    this.config = config;
  }

  /**
   * Start continuous monitoring and optimization
   */
  async start(): Promise<void> {
    console.log('🤖 AI Marketing Agent starting...');
    console.log(`📊 Monitoring frequency: Every ${this.config.monitoring_frequency_hours} hours`);
    console.log(`🎯 Autonomy mode: ${this.config.autonomy_mode}`);
    
    this.isMonitoring = true;

    // Run initial analysis
    await this.runAnalysisCycle();

    // Set up recurring monitoring
    const intervalMs = this.config.monitoring_frequency_hours * 60 * 60 * 1000;
    this.monitoringInterval = setInterval(async () => {
      if (this.isMonitoring) {
        await this.runAnalysisCycle();
      }
    }, intervalMs);

    console.log('✅ AI Marketing Agent is now active');
  }

  /**
   * Stop monitoring
   */
  stop(): void {
    console.log('🛑 Stopping AI Marketing Agent...');
    this.isMonitoring = false;
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }
    console.log('✅ AI Marketing Agent stopped');
  }

  /**
   * Run a complete analysis cycle
   */
  async runAnalysisCycle(): Promise<void> {
    console.log('\n' + '='.repeat(60));
    console.log('🔄 Starting AI Analysis Cycle');
    console.log('⏰ ' + new Date().toISOString());
    console.log('='.repeat(60));

    try {
      // Step 1: Run WordPress SEO Audits
      console.log('\n🔍 Step 1: Running WordPress SEO Audits...');
      const brands = ['hales_ac', 'climate_control_services', 'abraham_ac'];
      for (const brand of brands) {
        await this.runWordPressSEOAudits(brand);
      }

      // Step 2: Collect current KPIs
      console.log('\n📊 Step 2: Collecting KPIs...');
      const kpis = await this.collectAllKPIs();
      console.log(`   ✅ Collected ${kpis.length} KPIs across all brands`);

      // Step 3: Identify opportunities and threats
      // Step 3: Identify opportunities and threats
      console.log('\n🔍 Step 3: Analyzing trends...');
      const insights = await this.analyzeTrends(kpis);
      console.log(`   ✅ Identified ${insights.opportunities.length} opportunities`);
      console.log(`   ⚠️  Identified ${insights.threats.length} threats`);

      // Step 4: Make strategic decisions for each brand
      console.log('\n🧠 Step 4: Making strategic decisions...');
      
      for (const brand of brands) {
        const brandKPIs = kpis.filter(k => k.brand_key === brand);
        const brandInsights = {
          opportunities: insights.opportunities.filter(i => i.brand_key === brand),
          threats: insights.threats.filter(i => i.brand_key === brand)
        };

        if (brandKPIs.length === 0) {
          console.log(`   ⏭️  Skipping ${brand} - no KPI data`);
          continue;
        }

        console.log(`\n   🎯 Analyzing ${brand}...`);
        const decision = await this.makeStrategicDecision(brand, brandKPIs, brandInsights);
        
        if (decision) {
          console.log(`   ✅ Decision: ${decision.decision_summary}`);
          console.log(`   📊 Confidence: ${decision.confidence_score}%`);
          console.log(`   💡 Impact: ${decision.expected_impact}`);

          // Step 4: Execute or queue decision
          await this.processDecision(brand, decision);
        } else {
          console.log(`   ℹ️  No action needed - performance is optimal`);
        }
      }

      // Step 5: Check for scheduled actions to execute
      console.log('\n⚙️  Step 5: Executing scheduled actions...');
      await this.executeScheduledActions();

      // Step 6: Update learning from completed actions
      console.log('\n📚 Step 6: Learning from results...');
      await this.updateLearning();

      console.log('\n' + '='.repeat(60));
      console.log('✅ Analysis Cycle Complete');
      console.log('='.repeat(60) + '\n');

    } catch (error) {
      console.error('❌ Analysis cycle failed:', error);
      await this.sendAlert('Analysis Cycle Failed', error instanceof Error ? error.message : 'Unknown error');
    }
  }

  /**
   * Collect all KPIs from various sources
   */
  private async collectAllKPIs(): Promise<KPISnapshot[]> {
    const kpis: KPISnapshot[] = [];
    const brands = ['hales_ac', 'climate_control_services', 'abraham_ac'];

    for (const brand of brands) {
      // SEO KPIs from DataForSEO
      const seoKPIs = await this.collectSEOKPIs(brand);
      kpis.push(...seoKPIs);

      // Social KPIs (if enabled)
      const socialKPIs = await this.collectSocialKPIs(brand);
      kpis.push(...socialKPIs);

      // Engagement KPIs
      const engagementKPIs = await this.collectEngagementKPIs(brand);
      kpis.push(...engagementKPIs);
    }

    // Store in database
    for (const kpi of kpis) {
      await supabase.from('ai_agent_kpi_monitoring').insert({
        company_id: this.config.company_id,
        brand_key: kpi.brand_key,
        kpi_category: kpi.category,
        kpi_metric: kpi.metric,
        current_value: kpi.current_value,
        previous_value: kpi.previous_value,
        change_pct: kpi.change_pct,
        trend: kpi.trend,
        alert_status: kpi.alert_status,
        measurement_period: 'weekly',
        data_source: 'automated_collection',
        measured_at: new Date().toISOString()
      });
    }

    return kpis;
  }

  /**
   * Collect SEO KPIs from DataForSEO
   */
  private async collectSEOKPIs(brand: string): Promise<KPISnapshot[]> {
    const kpis: KPISnapshot[] = [];

    try {
      // Share of Search
      const { data: sosData } = await supabase
        .from('dataforseo_share_of_search')
        .select('share_of_search_pct, branded_searches')
        .eq('brand', brand)
        .order('period_start', { ascending: false })
        .limit(2);

      if (sosData && sosData.length > 0) {
        const current = sosData[0];
        const previous = sosData.length > 1 ? sosData[1] : null;
        
        kpis.push({
          brand_key: brand,
          category: 'seo',
          metric: 'share_of_search_pct',
          current_value: current.share_of_search_pct || 0,
          previous_value: previous?.share_of_search_pct || null,
          change_pct: previous ? 
            ((current.share_of_search_pct - previous.share_of_search_pct) / previous.share_of_search_pct * 100) : null,
          trend: this.determineTrend(current.share_of_search_pct, previous?.share_of_search_pct),
          alert_status: this.determineAlertStatus('share_of_search_pct', current.share_of_search_pct)
        });
      }

      // Visibility Score
      const { data: visData } = await supabase
        .from('dataforseo_visibility_metrics')
        .select('visibility_score, estimated_clicks')
        .eq('brand', brand)
        .order('period_start', { ascending: false })
        .limit(2);

      if (visData && visData.length > 0) {
        const current = visData[0];
        const previous = visData.length > 1 ? visData[1] : null;
        
        kpis.push({
          brand_key: brand,
          category: 'seo',
          metric: 'visibility_score',
          current_value: current.visibility_score || 0,
          previous_value: previous?.visibility_score || null,
          change_pct: previous ? 
            ((current.visibility_score - previous.visibility_score) / previous.visibility_score * 100) : null,
          trend: this.determineTrend(current.visibility_score, previous?.visibility_score),
          alert_status: this.determineAlertStatus('visibility_score', current.visibility_score)
        });
      }

      // Average Ranking Position
      const { data: rankData } = await supabase
        .from('dataforseo_rank_tracking')
        .select('current_position')
        .eq('brand', brand)
        .order('check_date', { ascending: false })
        .limit(100);

      if (rankData && rankData.length > 0) {
        const avgPosition = rankData.reduce((sum, r) => sum + r.current_position, 0) / rankData.length;
        
        kpis.push({
          brand_key: brand,
          category: 'seo',
          metric: 'avg_keyword_position',
          current_value: avgPosition,
          previous_value: null, // Would need historical comparison
          change_pct: null,
          trend: avgPosition < 10 ? 'improving' : avgPosition < 20 ? 'stable' : 'declining',
          alert_status: avgPosition < 10 ? 'normal' : avgPosition < 20 ? 'warning' : 'critical'
        });
      }

    } catch (error) {
      console.error(`Error collecting SEO KPIs for ${brand}:`, error);
    }

    return kpis;
  }

  /**
   * Collect Social Media KPIs
   */
  private async collectSocialKPIs(brand: string): Promise<KPISnapshot[]> {
    // TODO: Implement when social media APIs are connected
    return [];
  }

  /**
   * Collect Engagement KPIs
   */
  private async collectEngagementKPIs(brand: string): Promise<KPISnapshot[]> {
    const kpis: KPISnapshot[] = [];

    try {
      // Blog post performance
      const { data: blogData } = await supabase
        .from('marketing_blog_posts')
        .select('id, published_at')
        .eq('target_site', brand.replace('_', ''))
        .eq('status', 'published')
        .gte('published_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString());

      if (blogData) {
        kpis.push({
          brand_key: brand,
          category: 'engagement',
          metric: 'weekly_blog_posts',
          current_value: blogData.length,
          previous_value: null,
          change_pct: null,
          trend: blogData.length >= 2 ? 'improving' : blogData.length === 1 ? 'stable' : 'declining',
          alert_status: blogData.length >= 2 ? 'normal' : blogData.length === 1 ? 'warning' : 'critical'
        });
      }

      // WordPress SEO health scores
      const { data: seoAudits } = await supabase
        .from('wordpress_seo_audits')
        .select('overall_score, technical_score, on_page_score, content_score, performance_score')
        .eq('brand_key', brand)
        .order('audit_date', { ascending: false })
        .limit(2);

      if (seoAudits && seoAudits.length > 0) {
        const current = seoAudits[0];
        const previous = seoAudits.length > 1 ? seoAudits[1] : null;
        
        kpis.push({
          brand_key: brand,
          category: 'seo_health',
          metric: 'overall_seo_score',
          current_value: current.overall_score || 0,
          previous_value: previous?.overall_score || null,
          change_pct: previous ? 
            ((current.overall_score - previous.overall_score) / previous.overall_score * 100) : null,
          trend: this.determineTrend(current.overall_score, previous?.overall_score),
          alert_status: current.overall_score >= 80 ? 'normal' : current.overall_score >= 60 ? 'warning' : 'critical'
        });

        // Track specific SEO dimensions
        const dimensions: Array<'technical_score' | 'on_page_score' | 'content_score' | 'performance_score'> = 
          ['technical_score', 'on_page_score', 'content_score', 'performance_score'];
        
        dimensions.forEach(dimension => {
          const currentVal = current[dimension] || 0;
          const previousVal = previous?.[dimension] || null;
          
          kpis.push({
            brand_key: brand,
            category: 'seo_health',
            metric: dimension,
            current_value: currentVal,
            previous_value: previousVal,
            change_pct: previousVal ? ((currentVal - previousVal) / previousVal * 100) : null,
            trend: this.determineTrend(currentVal, previousVal),
            alert_status: currentVal >= 75 ? 'normal' : currentVal >= 50 ? 'warning' : 'critical'
          });
        });
      }

    } catch (error) {
      console.error(`Error collecting engagement KPIs for ${brand}:`, error);
    }

    return kpis;
  }

  /**
   * Run WordPress SEO audits for all brands (Phase 1 & 2)
   */
  private async runWordPressSEOAudits(brand: string): Promise<void> {
    try {
      console.log(`   🔍 WordPress SEO audit for ${brand} (skipped in browser)`);
      // WordPress optimizer functions are only available in Node.js backend
      console.log(`   ℹ️  WordPress optimization runs automatically via scheduled jobs`);
    } catch (error) {
      console.error(`   ❌ WordPress SEO audit failed for ${brand}:`, error);
    }
  }

  /**
   * Analyze trends and identify opportunities/threats
   */
  private async analyzeTrends(kpis: KPISnapshot[]): Promise<{
    opportunities: Array<{ brand_key: string; opportunity: string; priority: number }>;
    threats: Array<{ brand_key: string; threat: string; severity: number }>;
  }> {
    const opportunities: Array<{ brand_key: string; opportunity: string; priority: number }> = [];
    const threats: Array<{ brand_key: string; threat: string; severity: number }> = [];

    for (const kpi of kpis) {
      // Identify opportunities (improving trends)
      if (kpi.trend === 'improving' && kpi.change_pct && kpi.change_pct > 10) {
        opportunities.push({
          brand_key: kpi.brand_key,
          opportunity: `${kpi.metric} is up ${kpi.change_pct.toFixed(1)}% - capitalize on momentum`,
          priority: Math.min(Math.floor(kpi.change_pct / 10), 10)
        });
      }

      // Identify threats (declining trends or critical status)
      if (kpi.alert_status === 'critical' || (kpi.trend === 'declining' && kpi.change_pct && kpi.change_pct < -10)) {
        threats.push({
          brand_key: kpi.brand_key,
          threat: `${kpi.metric} is ${kpi.alert_status === 'critical' ? 'critical' : `down ${Math.abs(kpi.change_pct!).toFixed(1)}%`}`,
          severity: kpi.alert_status === 'critical' ? 10 : Math.min(Math.floor(Math.abs(kpi.change_pct || 0) / 10), 10)
        });
      }
    }

    return { opportunities, threats };
  }

  /**
   * Make strategic decision using AI analysis
   */
  private async makeStrategicDecision(
    brand: string,
    kpis: KPISnapshot[],
    insights: { opportunities: any[]; threats: any[] }
  ): Promise<AIDecision | null> {
    // Simple rule-based decisions (can be enhanced with GPT-4 API later)
    
    // Check for critical threats first
    const criticalThreats = insights.threats.filter(t => t.severity >= 8);
    if (criticalThreats.length > 0) {
      return {
        decision_type: 'optimization',
        confidence_score: 85,
        decision_summary: `Address critical performance issues in ${brand}`,
        recommended_actions: [{
          action_type: 'blog_post',
          action_config: { urgency: 'high', focus: 'recovery' },
          target_platform: 'wordpress'
        }],
        reasoning: `Critical metrics detected: ${criticalThreats.map(t => t.threat).join(', ')}`,
        expected_impact: 'high',
        expected_roi: 5000
      };
    }

    // Look for growth opportunities
    const highPriorityOpportunities = insights.opportunities.filter(o => o.priority >= 7);
    if (highPriorityOpportunities.length > 0) {
      return {
        decision_type: 'content',
        confidence_score: 75,
        decision_summary: `Capitalize on positive momentum for ${brand}`,
        recommended_actions: [{
          action_type: 'blog_post',
          action_config: { focus: 'growth', opportunity: highPriorityOpportunities[0].opportunity },
          target_platform: 'wordpress'
        }],
        reasoning: `Opportunities: ${highPriorityOpportunities.map(o => o.opportunity).join(', ')}`,
        expected_impact: 'medium',
        expected_roi: 2500
      };
    }

    // Check blog posting cadence
    const blogKPI = kpis.find(k => k.metric === 'weekly_blog_posts');
    if (blogKPI && blogKPI.current_value < 2) {
      return {
        decision_type: 'content',
        confidence_score: 90,
        decision_summary: `Increase content production for ${brand}`,
        recommended_actions: [{
          action_type: 'blog_post',
          action_config: { focus: 'consistency' },
          target_platform: 'wordpress'
        }],
        reasoning: 'Blog posting frequency below target (2 posts/week)',
        expected_impact: 'medium',
        expected_roi: 1500
      };
    }

    return null; // No action needed
  }

  /**
   * Process decision - approve or queue based on confidence
   */
  private async processDecision(brand: string, decision: AIDecision): Promise<void> {
    // Create decision record
    const { data: decisionRecord, error } = await supabase
      .from('ai_agent_decisions')
      .insert({
        company_id: this.config.company_id,
        brand_key: brand,
        decision_type: decision.decision_type,
        analysis_context: { timestamp: new Date().toISOString() },
        confidence_score: decision.confidence_score,
        expected_impact: decision.expected_impact,
        expected_roi: decision.expected_roi,
        decision_summary: decision.decision_summary,
        recommended_actions: decision.recommended_actions,
        reasoning: decision.reasoning,
        status: decision.confidence_score >= this.config.auto_approve_threshold ? 'approved' : 'pending',
        auto_approved: decision.confidence_score >= this.config.auto_approve_threshold
      })
      .select()
      .single();

    if (error || !decisionRecord) {
      console.error('Failed to create decision record:', error);
      return;
    }

    // If auto-approved, create action records
    if (decisionRecord.auto_approved) {
      console.log(`   ✅ Auto-approved (confidence ${decision.confidence_score}%)`);
      for (const action of decision.recommended_actions) {
        await this.queueAction(decisionRecord.id, brand, action);
      }
    } else {
      console.log(`   ⏸️  Queued for review (confidence ${decision.confidence_score}%)`);
    }
  }

  /**
   * Queue an action for execution
   */
  private async queueAction(decisionId: string, brand: string, action: Action): Promise<void> {
    await supabase.from('ai_agent_actions').insert({
      decision_id: decisionId,
      company_id: this.config.company_id,
      action_type: action.action_type,
      action_config: action.action_config,
      content_title: action.content_title,
      content_body: action.content_body,
      target_platform: action.target_platform,
      target_channel: brand,
      scheduled_for: action.scheduled_for || new Date(Date.now() + 60 * 60 * 1000).toISOString(), // 1 hour from now
      status: 'queued'
    });
  }

  /**
   * Execute scheduled actions
   */
  private async executeScheduledActions(): Promise<void> {
    const { data: actions } = await supabase
      .from('ai_agent_actions')
      .select('*')
      .eq('status', 'queued')
      .lte('scheduled_for', new Date().toISOString())
      .limit(10);

    if (!actions || actions.length === 0) {
      console.log('   ℹ️  No actions ready to execute');
      return;
    }

    console.log(`   📤 Executing ${actions.length} queued actions...`);

    for (const action of actions) {
      try {
        await supabase
          .from('ai_agent_actions')
          .update({ status: 'executing', executed_at: new Date().toISOString() })
          .eq('id', action.id);

        // Execute based on action type
        if (action.action_type === 'blog_post') {
          // Trigger blog automation
          console.log(`      ✅ Triggered blog post for ${action.target_channel}`);
        }

        await supabase
          .from('ai_agent_actions')
          .update({ 
            status: 'completed',
            completed_at: new Date().toISOString(),
            metrics: { executed: true }
          })
          .eq('id', action.id);

      } catch (error) {
        console.error(`      ❌ Action ${action.id} failed:`, error);
        await supabase
          .from('ai_agent_actions')
          .update({ 
            status: 'failed',
            error_message: error instanceof Error ? error.message : 'Unknown error'
          })
          .eq('id', action.id);
      }
    }
  }

  /**
   * Update learning from completed actions
   */
  private async updateLearning(): Promise<void> {
    // TODO: Implement learning algorithm
    // - Analyze completed actions and their results
    // - Identify patterns
    // - Store validated insights
    console.log('   📚 Learning system ready for implementation');
  }

  /**
   * Send alert email
   */
  private async sendAlert(subject: string, message: string): Promise<void> {
    console.log(`🚨 ALERT: ${subject}`);
    console.log(`   ${message}`);
    // TODO: Integrate with email service
  }

  /**
   * Determine trend from current and previous values
   */
  private determineTrend(current: number, previous: number | null | undefined): 'improving' | 'declining' | 'stable' | 'volatile' {
    if (!previous) return 'stable';
    
    const changePct = ((current - previous) / previous) * 100;
    
    if (Math.abs(changePct) > 20) return 'volatile';
    if (changePct > 5) return 'improving';
    if (changePct < -5) return 'declining';
    return 'stable';
  }

  /**
   * Determine alert status for a metric
   */
  private determineAlertStatus(metric: string, value: number): 'normal' | 'warning' | 'critical' {
    // Define thresholds per metric
    const thresholds: Record<string, { warning: number; critical: number }> = {
      share_of_search_pct: { warning: 10, critical: 5 },
      visibility_score: { warning: 50, critical: 25 },
      avg_keyword_position: { warning: 20, critical: 30 }
    };

    const threshold = thresholds[metric];
    if (!threshold) return 'normal';

    if (metric === 'avg_keyword_position') {
      // Higher is worse for position
      if (value >= threshold.critical) return 'critical';
      if (value >= threshold.warning) return 'warning';
      return 'normal';
    } else {
      // Lower is worse for most metrics
      if (value <= threshold.critical) return 'critical';
      if (value <= threshold.warning) return 'warning';
      return 'normal';
    }
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Start the AI Marketing Agent
 */
export async function startAIAgent(companyId: string): Promise<AIMarketingAgent> {
  // Load configuration
  const { data: config } = await supabase
    .from('ai_agent_config')
    .select('*')
    .eq('company_id', companyId)
    .single();

  if (!config) {
    throw new Error('AI Agent configuration not found');
  }

  const agent = new AIMarketingAgent({
    company_id: companyId,
    autonomy_mode: config.autonomy_mode,
    auto_approve_threshold: config.auto_approve_threshold,
    daily_action_limit: config.daily_action_limit,
    monitoring_frequency_hours: config.monitoring_frequency_hours,
    alert_email_addresses: config.alert_email_addresses,
    primary_goals: config.primary_goals,
    openai_api_key: config.openai_api_key
  });

  await agent.start();
  
  return agent;
}

/**
 * Stop the AI Marketing Agent
 */
export function stopAIAgent(agent: AIMarketingAgent): void {
  agent.stop();
}
